import React, { useState, useEffect, useCallback } from 'react'
import DataTable from '../components/DataTable'
import Modal from '../components/Modal'
import FormField from '../components/FormField'

interface Customer {
  id: number
  company_name: string
  name: string
  name_kana: string
  postal_code: string
  address: string
  phone: string
  fax: string
  email: string
  notes: string
  created_at: string
  updated_at: string
}

const emptyCustomer: Omit<Customer, 'id' | 'created_at' | 'updated_at'> = {
  company_name: '',
  name: '',
  name_kana: '',
  postal_code: '',
  address: '',
  phone: '',
  fax: '',
  email: '',
  notes: ''
}

const Customer: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null)
  const [formData, setFormData] = useState(emptyCustomer)
  const [error, setError] = useState('')

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true)
      const result = await window.api.customer.list({ page, limit: 20, search: search || undefined })
      if (result.ok) {
        const data = result.data as { rows: Customer[]; total: number }
        setCustomers(data.rows)
        setTotal(data.total)
      } else {
        setError(result.error as string)
      }
    } catch (err) {
      setError(String(err))
    } finally {
      setLoading(false)
    }
  }, [page, search])

  useEffect(() => {
    loadCustomers()
  }, [loadCustomers])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    setPage(1)
  }

  const openCreateModal = () => {
    setEditingCustomer(null)
    setFormData(emptyCustomer)
    setShowModal(true)
  }

  const openEditModal = (customer: Customer) => {
    setEditingCustomer(customer)
    setFormData({
      company_name: customer.company_name,
      name: customer.name,
      name_kana: customer.name_kana,
      postal_code: customer.postal_code,
      address: customer.address,
      phone: customer.phone,
      fax: customer.fax,
      email: customer.email,
      notes: customer.notes
    })
    setShowModal(true)
  }

  const handleSubmit = async () => {
    try {
      if (editingCustomer) {
        const result = await window.api.customer.update(editingCustomer.id, formData)
        if (!result.ok) {
          setError(result.error as string)
          return
        }
      } else {
        const result = await window.api.customer.create(formData)
        if (!result.ok) {
          setError(result.error as string)
          return
        }
      }
      setShowModal(false)
      loadCustomers()
    } catch (err) {
      setError(String(err))
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('この顧客を削除してもよろしいですか？\n（論理削除されます）')) {
      return
    }
    try {
      const result = await window.api.customer.delete(id)
      if (result.ok) {
        loadCustomers()
      } else {
        setError(result.error as string)
      }
    } catch (err) {
      setError(String(err))
    }
  }

  const columns = [
    { key: 'id', label: 'ID', width: 60, align: 'right' as const },
    { key: 'company_name', label: '会社名', width: 200, align: 'left' as const },
    { key: 'name', label: '担当者名', width: 150, align: 'left' as const },
    { key: 'phone', label: '電話番号', width: 130, align: 'left' as const },
    { key: 'email', label: 'メールアドレス', width: 200, align: 'left' as const },
    { key: 'address', label: '住所', width: 250, align: 'left' as const }
  ]

  const totalPages = Math.ceil(total / 20)

  return (
    <div className="page customer-page">
      <div className="page-header">
        <h2 className="page-title">顧客マスタ</h2>
        <button className="btn btn-primary" onClick={openCreateModal}>
          新規顧客登録
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="顧客名・会社名・電話番号で検索..."
          value={search}
          onChange={handleSearch}
          className="search-input"
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      <DataTable
        columns={columns}
        data={customers}
        loading={loading}
        onEdit={openEditModal}
        onDelete={(row) => handleDelete(row.id)}
        emptyMessage="顧客データがありません"
      />

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            前へ
          </button>
          <span className="page-info">{page} / {totalPages} ページ</span>
          <button
            className="btn btn-secondary"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            次へ
          </button>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingCustomer ? '顧客情報編集' : '新規顧客登録'}
      >
        <div className="form-grid">
          <FormField label="会社名" required>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="input"
            />
          </FormField>
          <FormField label="担当者名" required>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
            />
          </FormField>
          <FormField label="担当者名（カナ）">
            <input
              type="text"
              value={formData.name_kana}
              onChange={(e) => setFormData({ ...formData, name_kana: e.target.value })}
              className="input"
            />
          </FormField>
          <FormField label="郵便番号">
            <input
              type="text"
              value={formData.postal_code}
              onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
              className="input"
              placeholder="123-4567"
            />
          </FormField>
          <FormField label="住所" fullWidth>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="input"
            />
          </FormField>
          <FormField label="電話番号" required>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
            />
          </FormField>
          <FormField label="FAX">
            <input
              type="text"
              value={formData.fax}
              onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
              className="input"
            />
          </FormField>
          <FormField label="メールアドレス">
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
            />
          </FormField>
          <FormField label="備考" fullWidth>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input textarea"
              rows={3}
            />
          </FormField>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
            キャンセル
          </button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            {editingCustomer ? '更新' : '登録'}
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default Customer
